import React, {
  createContext, useCallback, useContext, useEffect, useState,
} from 'react';
import { backendProfileChangedEventName, profilesApi } from '../../api/profiles';
import { useAuth } from '../../contexts/AuthContext';
import { invalidatePageCache } from '../../services/pageCache';
import { updateCachedAuthor } from '../../services/homeFeedSession';
import { subscribeRealtime } from '../../services/realtime';

export const UserContext = createContext();
export const useUser = () => useContext(UserContext);
// Kept only until the legacy multi-account module is removed from the bundle.
// Backend authentication never calls this Firebase bridge.
export const CUSTOM_TOKEN_URL = 'https://login-api.amposter.com/get-token';

export const UserProvider = ({ children }) => {
  const { currentUserId, loading: authLoading, isAuthenticated } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState('loading');
  const [profileRetryKey, setProfileRetryKey] = useState(0);
  const [livePresence, setLivePresence] = useState(null);

  useEffect(() => {
    setLivePresence(null);
    if (!currentUserId) return;
    let active = true;
    const unsubscribe = subscribeRealtime(event => {
      if (event.type === 'profile.media.changed' && event.data?.userId === currentUserId) {
        invalidatePageCache(currentUserId, true, ['profiles', 'posts']);
        profilesApi.me().then(profile => {
          if (!active) return;
          setUserData(profile);
          updateCachedAuthor(currentUserId, { avatarUrl: profile.avatarUrl, sensitiveAvatar: profile.sensitiveAvatar });
          window.dispatchEvent(new Event('amposter:profile-media-review-changed'));
        }).catch(() => {});
      }
      if (['ready', 'pong'].includes(event.type)
          && ['online', 'offline'].includes(event.data?.presenceStatus)) {
        setLivePresence({ userId: currentUserId, status: event.data.presenceStatus });
      }
    });
    return () => { active = false; unsubscribe(); };
  }, [currentUserId]);

  const retryProfile = useCallback(() => {
    setProfileStatus('loading');
    setLoading(true);
    setProfileRetryKey((value) => value + 1);
  }, []);

  useEffect(() => {
    let mounted = true;
    if (authLoading) {
      setLoading(true);
      setProfileStatus('loading');
      return () => { mounted = false; };
    }
    if (!isAuthenticated || !currentUserId) {
      setUserData(null);
      setProfileStatus('signed-out');
      setLoading(false);
      return () => { mounted = false; };
    }

    setLoading(true);
    profilesApi.me(profileRetryKey > 0 ? { cache: 'reload' } : undefined)
      .then((profile) => {
        if (!mounted) return;
        setUserData(profile);
        setProfileStatus(profile?.completed_profile ? 'ready' : 'missing');
      })
      .catch((error) => {
        if (!mounted) return;
        console.error('Backend profile could not be loaded:', error);
        setUserData(null);
        setProfileStatus('error');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, [authLoading, currentUserId, isAuthenticated, profileRetryKey]);

  useEffect(() => {
    const refresh = (event) => {
      if (event.detail) {
        setUserData(event.detail);
        setProfileStatus(event.detail.completed_profile ? 'ready' : 'missing');
        setLoading(false);
      } else {
        retryProfile();
      }
    };
    window.addEventListener(backendProfileChangedEventName, refresh);
    return () => window.removeEventListener(backendProfileChangedEventName, refresh);
  }, [retryProfile]);

  useEffect(() => {
    const changed = ({ detail }) => setUserData(profile => profile?.id === detail?.userId ? { ...profile, verified: detail.verified } : profile);
    window.addEventListener('amposter:verification-changed', changed);
    return () => window.removeEventListener('amposter:verification-changed', changed);
  }, []);

  // A startup profile request may finish before or after the socket handshake.
  // Keep the confirmed socket status without fetching the profile again.
  const currentProfile = userData && livePresence?.userId === userData.id
    ? { ...userData, presenceStatus: livePresence.status, status: livePresence.status }
    : userData;
  return (
    <UserContext.Provider value={{ userData: currentProfile, setUserData, loading, profileStatus, retryProfile }}>
      {children}
    </UserContext.Provider>
  );
};
